import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

export interface SwaUser {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

interface SwaAuthResponse {
  clientPrincipal: SwaUser | null;
}

let cached: SwaUser | null | undefined;

export async function getUser(): Promise<SwaUser | null> {
  if (cached !== undefined) return cached;
  try {
    const res = await fetch('/.auth/me', { credentials: 'include' });
    if (!res.ok) {
      cached = null;
      return null;
    }
    const data = (await res.json()) as SwaAuthResponse;
    cached = data.clientPrincipal;
    return cached;
  } catch {
    cached = null;
    return null;
  }
}

export function logout() {
  window.location.href = '/.auth/logout?post_logout_redirect_uri=/login';
}

// Liste d'emails autorisés (dev = juste l'admin, prod = vrais comptes collaborateurs).
// Surchargeable via VITE_ALLOWED_EMAILS (CSV) au build.
const ALLOWED_EMAILS = (
  import.meta.env.VITE_ALLOWED_EMAILS ?? 'rk@stella-cars.com,r.khadraoui@alfunservices.cloud'
)
  .toLowerCase()
  .split(',')
  .map((e: string) => e.trim())
  .filter(Boolean);

// Domaines autorisés (tout compte du tenant Stella est accepté).
const ALLOWED_DOMAINS = (import.meta.env.VITE_ALLOWED_DOMAINS ?? 'stella-cars.com')
  .toLowerCase()
  .split(',')
  .map((d: string) => d.trim())
  .filter(Boolean);

export function isEmailAllowed(user: SwaUser | null): boolean {
  if (!user?.userDetails) return false;
  const id = user.userDetails.toLowerCase();
  if (ALLOWED_EMAILS.includes(id)) return true;
  return ALLOWED_DOMAINS.some((d: string) => id.endsWith('@' + d));
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'in' | 'denied' | 'out'>('loading');
  const [user, setUser] = useState<SwaUser | null>(null);

  useEffect(() => {
    getUser().then((u) => {
      setUser(u);
      if (!u) return setState('out');
      setState(isEmailAllowed(u) ? 'in' : 'denied');
    });
  }, []);

  if (state === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-muted-dark">
        Vérification de la session…
      </div>
    );
  }
  if (state === 'out') return <Navigate to="/login" replace />;
  if (state === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary-bg px-4">
        <div className="w-full max-w-md rounded-lg border border-white/5 bg-primary-surface p-8 text-center shadow-lg">
          <div className="mb-2 font-display text-xl text-cream">Accès refusé</div>
          <p className="mb-4 text-sm text-muted-dark">
            Votre compte n'est pas autorisé à accéder au back-office STELLA CARS.
          </p>
          {user?.userDetails ? (
            <p className="mb-6 text-xs text-muted-dark">
              Connecté en tant que : <code className="text-cream/80">{user.userDetails}</code>
            </p>
          ) : null}
          <a
            href="/.auth/logout?post_logout_redirect_uri=/login"
            className="inline-block rounded-md bg-accent-gold px-4 py-2 text-sm font-medium text-primary-bg hover:bg-accent-goldLight"
          >
            Se déconnecter
          </a>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
